import { useMutation, useQueryClient } from '@tanstack/react-query'
import { companyRequest } from '@/shared/api'
import { type Project, type ProjectInput, ProjectResponseSchema } from '../model/types'
import { projectKeys } from './queries'

// Проекты влияют и на карточку компании — инвалидируем оба namespace'а.
// Ключ компании — литерал, чтобы не тянуть чужой слайс (FSD).
function invalidate(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: projectKeys.all })
  void qc.invalidateQueries({ queryKey: ['company'] })
}

export function useCreateProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: ProjectInput): Promise<Project> => {
      const body = await companyRequest<unknown>({ method: 'POST', path: '/projects', body: input })
      return ProjectResponseSchema.parse(body).data
    },
    onSuccess: () => invalidate(qc),
  })
}

export function useUpdateProject(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: ProjectInput): Promise<Project> => {
      const body = await companyRequest<unknown>({
        method: 'PATCH',
        path: `/projects/${id}`,
        body: input,
      })
      return ProjectResponseSchema.parse(body).data
    },
    onSuccess: () => invalidate(qc),
  })
}

export function useArchiveProject(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (archived: boolean): Promise<void> => {
      await companyRequest<unknown>({
        method: 'POST',
        path: `/projects/${id}/${archived ? 'archive' : 'unarchive'}`,
      })
    },
    onSuccess: () => invalidate(qc),
  })
}

// Перенос проекта между компаниями (PATCH /projects/:id/company).
export function useMoveProject(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (companyId: string): Promise<Project> => {
      const body = await companyRequest<unknown>({
        method: 'PATCH',
        path: `/projects/${id}/company`,
        body: { company_id: companyId },
      })
      return ProjectResponseSchema.parse(body).data
    },
    onSuccess: () => invalidate(qc),
  })
}
